// =============================================================================
// VandieZ shipper-web -- Login page (/login)
// =============================================================================
// Three-step phone-OTP login/signup, matching the mobile-shipper flow:
//
//   Step "phone": collect phone, POST /auth/otp/request, advance to "otp".
//   Step "otp":   collect the 6-digit code, POST /auth/otp/verify with
//                 primary_role='shipper'. On 422 + missing_field='full_name'
//                 (new account), advance to "name". On success, store the
//                 session and redirect.
//   Step "name":  collect full_name once for new accounts, retry
//                 /auth/otp/verify with the same session/code + role + name.
//                 The backend peeks the session before consuming so the
//                 retry is safe.
// =============================================================================

import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { authApi } from "@/api/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";

type Step = "phone" | "otp" | "name";

interface LocationState {
  from?: string;
}

function normalizePhoneInput(raw: string): string {
  // Strip whitespace; users often paste with spaces.
  const trimmed = raw.replace(/\s+/g, "");
  // If they typed 10 digits and no country code, default to +91 (India).
  if (/^\d{10}$/.test(trimmed)) {
    return `+91${trimmed}`;
  }
  return trimmed;
}

export function LoginPage(): React.JSX.Element {
  const { user, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTarget = (location.state as LocationState | null)?.from ?? "/";

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Centralised verify: used both for the first attempt (no name yet) and
  // the second attempt after the user types their name. The backend peeks
  // the OTP session before validating signup fields, so retrying with the
  // same session_id + code is safe when the first attempt 422'd on
  // missing_field=full_name.
  async function callVerify(name: string | null): Promise<void> {
    if (otpSessionId === null) {
      setError("Session expired -- request a new code.");
      setStep("phone");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await authApi.verifyOtp({
        otp_session_id: otpSessionId,
        otp_code: otpCode,
        primary_role: "shipper",
        ...(name !== null && name.trim().length > 0
          ? { full_name: name.trim() }
          : {}),
      });
      setSession({
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      });
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.problem.status === 422 &&
        err.problem["missing_field"] === "full_name"
      ) {
        setStep("name");
        setError(null);
        return;
      }
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Already logged in? Don't show the form -- bounce straight in.
  if (user !== null) {
    return <Navigate to={redirectTarget} replace />;
  }

  async function handleRequestOtp(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    const normalized = normalizePhoneInput(phone);
    if (!/^\+\d{10,15}$/.test(normalized)) {
      setError("Enter a valid phone number with country code (e.g., +91XXXXXXXXXX).");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authApi.requestOtp({
        phone: normalized,
        purpose: "login",
      });
      setOtpSessionId(response.otp_session_id);
      setMaskedPhone(response.masked_phone);
      setPhone(normalized);
      setStep("otp");
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyOtp(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!/^\d{4,8}$/.test(otpCode)) {
      setError("Enter the OTP code (digits only).");
      return;
    }
    await callVerify(null);
  }

  async function handleSubmitName(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (fullName.trim().length === 0) {
      setError("Enter your full name to create the account.");
      return;
    }
    await callVerify(fullName);
  }

  function handleBackToPhone(): void {
    setStep("phone");
    setOtpCode("");
    setOtpSessionId(null);
    setMaskedPhone(null);
    setFullName("");
    setError(null);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            VandieZ
          </p>
          <CardTitle>Shipper sign in</CardTitle>
          <CardDescription>
            {step === "phone"
              ? "Enter your phone number; we will text you a one-time code."
              : step === "otp"
                ? `Enter the 6-digit code sent to ${maskedPhone ?? "your phone"}.`
                : "Welcome! Tell us your name to create the account."}
          </CardDescription>
        </CardHeader>

        {step === "phone" ? (
          <form onSubmit={handleRequestOtp}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+91XXXXXXXXXX"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); }}
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground">
                  10-digit Indian numbers are auto-prefixed with +91.
                </p>
              </div>

              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>

            <CardFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  "Send code"
                )}
              </Button>
            </CardFooter>
          </form>
        ) : step === "otp" ? (
          <form onSubmit={handleVerifyOtp}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">One-time code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d*"
                  maxLength={8}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "")); }}
                  required
                  autoFocus
                  disabled={submitting}
                />
              </div>

              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleBackToPhone}
                disabled={submitting}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4" />
                Use a different number
              </Button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleSubmitName}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Anand Ram"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); }}
                  required
                  autoFocus
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground">
                  We only ask once, when creating your account.
                </p>
              </div>

              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>

            <CardFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // For VALIDATION_ERROR, the first errors[] entry is usually the most
    // actionable.
    if (err.problem.errors !== undefined && err.problem.errors.length > 0) {
      const first = err.problem.errors[0];
      if (first !== undefined) {
        return first.msg;
      }
    }
    return err.problem.detail;
  }
  if (err instanceof Error) {return err.message;}
  return "Something went wrong. Please try again.";
}
